import { z } from 'zod'

export const membershipTierRequiredSchema = z.enum(['free', 'premium'])

export const createAdminScheduledSessionSchema = z
  .object({
    title: z.string().min(1).max(200),
    session_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    session_time: z.string().regex(/^\d{2}:\d{2}$/),
    duration_minutes: z.number().int().min(15).max(480).optional(),
    tier_required: membershipTierRequiredSchema,
    zoom_link: z.string().url(),
    zoom_meeting_id: z.string().max(50).optional(),
    passcode: z.string().max(50).optional(),
    is_recurring: z.boolean().default(false),
    recurrence_days: z.array(z.number().int().min(0).max(6)).optional(),
    end_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    same_zoom_link_for_all: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (data.is_recurring) {
      if (!data.end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'end_date is required for recurring sessions',
          path: ['end_date'],
        })
      }
      if (!data.recurrence_days?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one weekday is required for recurring sessions',
          path: ['recurrence_days'],
        })
      }
      if (data.end_date && data.end_date < data.session_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'end_date must be on or after session_date',
          path: ['end_date'],
        })
      }
    }
  })

export const updateAdminScheduledSessionSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  session_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  session_time: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  duration_minutes: z.number().int().min(15).max(480).nullable().optional(),
  tier_required: membershipTierRequiredSchema.optional(),
  zoom_link: z.string().url().optional(),
  zoom_meeting_id: z.string().max(50).nullable().optional(),
  passcode: z.string().max(50).nullable().optional(),
  status: z.enum(['scheduled', 'completed', 'cancelled']).optional(),
})

export const updateRecurrenceGroupSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  session_time: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  duration_minutes: z.number().int().min(15).max(480).nullable().optional(),
  tier_required: membershipTierRequiredSchema.optional(),
  zoom_link: z.string().url().optional(),
  zoom_meeting_id: z.string().max(50).nullable().optional(),
  passcode: z.string().max(50).nullable().optional(),
})

export type CreateAdminScheduledSessionInput = z.infer<
  typeof createAdminScheduledSessionSchema
>
export type UpdateAdminScheduledSessionInput = z.infer<
  typeof updateAdminScheduledSessionSchema
>
export type UpdateRecurrenceGroupInput = z.infer<
  typeof updateRecurrenceGroupSchema
>
